import moment from "moment";
import { API_DATE } from "../Shared/formatters";
import { round2 } from "./loansData";

// Column mapping + checks for Pay Inputs > Loans & Advances > Import. Each
// row becomes a new advance / loan request through
// POST advance/request/import/ (Pending, sent to the employee's approver).
// Same matching rules as the other Pay Inputs imports: a column is matched
// by its header against the field label + aliases, then by its values.
// Rows that pass the checks here go to .../import/validate/ before the
// preview; the server checks the employee and does the final checks.
//
// Salary requests only, same as Add Loan / Advance: Business advances are
// settled against expense claims, not payroll, so they aren't a pay input.
// A Salary Loan gets a repayment schedule (repayment mode + date, and
// instalments for Installment mode). A Salary Advance is recovered in full
// in one payroll month: repayment_mode "Full" and repayment_date = that
// month's last day, same as Add Loan / Advance.

export { applyServerChecks } from "../Shared/importMapping";

export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const IMPORT_ACCEPT = ".xlsx,.xls,.csv";

export const FREQUENCIES = ["Weekly", "Bi-weekly", "Semi-monthly", "Monthly"];

export const IMPORT_FIELDS = [
  { key: "employee_id", label: "Employee ID", required: true, aliases: ["employee id", "employee code", "emp id", "emp code", "emp no", "employee no", "staff id"] },
  { key: "type", label: "Type", required: false, aliases: ["type", "record type", "loan or advance", "request type", "category"] },
  { key: "amount", label: "Amount", required: true, aliases: ["amount", "loan amount", "advance amount", "requested amount", "principal"] },
  { key: "description", label: "Description", required: false, aliases: ["description", "reference", "description reference", "loan description", "advance description"] },
  { key: "reason", label: "Reason", required: true, aliases: ["reason", "remarks", "notes", "comments", "purpose"] },
  { key: "request_date", label: "Request Date", required: false, aliases: ["request date", "date", "applied on", "requested on"] },
  { key: "payroll_month", label: "Payroll Month", required: false, aliases: ["payroll month", "pay month", "salary month", "recovery month", "deduction month"] },
  { key: "repayment_mode", label: "Repayment Mode", required: false, aliases: ["repayment mode", "repayment", "mode", "recovery mode"] },
  { key: "repayment_date", label: "Repayment Date", required: false, aliases: ["repayment date", "repayment start date", "recovery date", "first repayment date", "recovery start date"] },
  { key: "repayment_frequency", label: "Repayment Frequency", required: false, aliases: ["repayment frequency", "frequency", "recovery frequency"] },
  { key: "no_of_installments", label: "No. of Instalments", required: false, aliases: ["no of installments", "no of instalments", "installments", "instalments", "number of installments", "emi count", "emis"] },
];

export const IMPORT_GUIDELINES = [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "Each row creates a new request with status Pending. It goes to the employee's approver; nothing is approved by the import.",
  "Employee ID is the employee code (e.g. EMP001). Type is Advance or Loan (blank = Advance). All requests are salary loans / advances.",
  "Amount (more than 0) and Reason are required. Description is optional (up to 200 characters). Request Date blank = today.",
  "Loan: Repayment Mode (Full or Installment) and Repayment Date are required; Installment also needs No. of Instalments (2 or more). Repayment Frequency: Weekly, Bi-weekly, Semi-monthly or Monthly (blank = Monthly).",
  "Advance: Payroll Month (e.g. Oct 2026; blank = current month). It is recovered in full in that payroll, so the repayment date is the month's last day. Repayment columns are ignored for an Advance.",
  "Dates: DD MMM YYYY (e.g. 15 Jan 2026), DD-MM-YYYY or YYYY-MM-DD. Maximum file size: 5 MB.",
];

const normalize = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const words = (value) => String(value ?? "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

// Dice coefficient on letter pairs: 1 = identical, 0 = nothing shared.
const pairSimilarity = (a, b) => {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const pairs = (text) => Array.from({ length: text.length - 1 }, (_, i) => text.slice(i, i + 2));
  const left = pairs(a);
  const pool = pairs(b);
  const total = left.length + pool.length;
  let shared = 0;
  left.forEach((pair) => {
    const at = pool.indexOf(pair);
    if (at !== -1) {
      shared += 1;
      pool.splice(at, 1);
    }
  });
  return (2 * shared) / total;
};

const headerScore = (header, field) => {
  const name = normalize(header);
  if (!name) return 0;
  const headerWords = words(header);
  let best = 0;
  [field.label, ...field.aliases].forEach((candidate) => {
    const target = normalize(candidate);
    if (name === target) {
      best = Math.max(best, 1);
      return;
    }
    const targetWords = words(candidate);
    if (targetWords.length && targetWords.every((word) => headerWords.includes(word))) {
      best = Math.max(best, 0.9 - 0.02 * (headerWords.length - targetWords.length));
    }
    best = Math.max(best, pairSimilarity(name, target) * 0.85);
  });
  return best;
};

const isBlankCell = (value) => value === null || value === undefined || String(value).trim() === "";

const DATE_FORMATS = ["DD MMM YYYY", "D MMM YYYY", API_DATE, "DD-MM-YYYY", "DD/MM/YYYY"];

export const parseDate = (value) => {
  const parsed = moment(String(value ?? "").trim(), DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(API_DATE) : null;
};

// Payroll Month: a month ("Oct 2026", "October 2026", "10-2026", "2026-10")
// or any date in it. Returns "YYYY-MM".
const MONTH_FORMATS = ["MMM YYYY", "MMMM YYYY", "MMM-YYYY", "MMMM-YYYY", "MM-YYYY", "M-YYYY", "MM/YYYY", "M/YYYY", "YYYY-MM", ...DATE_FORMATS];
export const parseMonth = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return moment(value).format("YYYY-MM");
  const parsed = moment(String(value ?? "").trim(), MONTH_FORMATS, true);
  return parsed.isValid() ? parsed.format("YYYY-MM") : null;
};

const pick = (aliases) => (value) => {
  const text = normalize(value);
  return Object.entries(aliases).find(([, names]) => names.includes(text))?.[0] ?? null;
};
// Values the checks accept, in the API's spelling (the import's Edit panel
// offers these in its pickers).
export const TYPES = ["Advance", "Loan"];
export const REPAYMENT_MODES = ["Full", "Installment"];

export const matchType = pick({ Loan: ["loan", "loans"], Advance: ["advance", "advances", "cashadvance"] });
export const matchMode = pick({ Full: ["full", "fullpayment", "onetime", "lumpsum"], Installment: ["installment", "installments", "instalment", "instalments", "emi"] });
export const matchFrequency = pick({ Weekly: ["weekly"], "Bi-weekly": ["biweekly", "fortnightly"], "Semi-monthly": ["semimonthly"], Monthly: ["monthly"] });

const CONTENT_RULES = {
  type: (value) => Boolean(matchType(value)),
  repayment_mode: (value) => Boolean(matchMode(value)),
  repayment_frequency: (value) => Boolean(matchFrequency(value)),
};

const contentScore = (rows, index, test) => {
  const sample = rows.map(({ cells }) => cells[index]).filter((value) => !isBlankCell(value)).slice(0, 50);
  if (!sample.length) return 0;
  return sample.filter((value) => test(value)).length / sample.length;
};

// { [columnIndex]: fieldKey }; each field and column used at most once.
export const autoMapColumns = (headers, rows = []) => {
  const mapping = {};
  const usedFields = new Set();
  const usedColumns = new Set();
  const assign = (candidates, minimum) => {
    candidates
      .filter((candidate) => candidate.score >= minimum)
      .sort((a, b) => b.score - a.score)
      .forEach(({ index, key }) => {
        if (usedFields.has(key) || usedColumns.has(index)) return;
        mapping[index] = key;
        usedFields.add(key);
        usedColumns.add(index);
      });
  };
  const byHeader = [];
  headers.forEach((header, index) => {
    IMPORT_FIELDS.forEach((field) => byHeader.push({ index, key: field.key, score: headerScore(header, field) }));
  });
  assign(byHeader, 0.6);
  const byContent = [];
  headers.forEach((header, index) => {
    if (usedColumns.has(index)) return;
    Object.entries(CONTENT_RULES).forEach(([key, test]) => {
      if (!usedFields.has(key)) byContent.push({ index, key, score: contentScore(rows, index, test) });
    });
  });
  assign(byContent, 0.6);
  return mapping;
};

export const missingRequiredFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
};

// First non-empty row is the header row; fully blank rows are skipped.
export const splitSheetRows = (sheetRows) => {
  const isBlank = (row) => !row || row.every(isBlankCell);
  const headerIndex = sheetRows.findIndex((row) => !isBlank(row));
  if (headerIndex === -1) return { headers: [], rows: [] };
  const headers = sheetRows[headerIndex].map((cell) => String(cell ?? "").trim());
  const rows = [];
  sheetRows.slice(headerIndex + 1).forEach((cells) => {
    if (!isBlank(cells)) rows.push({ rowNumber: rows.length + 1, cells });
  });
  return { headers, rows };
};

const toNumber = (value) => (isBlankCell(value) ? NaN : Number(String(value).replace(/[,₹\s]/g, "")));

// Returns { rowNumber, raw, errors, row } per data row, checked in the API's
// order (section 5 of the import API). `row` is the import record (valid
// rows only), holding only the fields that apply to its type.
export const validateImportRows = (rows, mapping) =>
  rows.map(({ rowNumber, cells }) => {
    const raw = {};
    Object.entries(mapping).forEach(([index, key]) => {
      raw[key] = cells[Number(index)];
    });
    return checkImportRow(rowNumber, raw);
  });

// Checks one row's values ({ [fieldKey]: value }). Also used to re-check a
// row corrected in the preview's Edit panel. Each row is checked on its own
// (no checks across rows).
export const checkImportRow = (rowNumber, raw) => {
  const errors = [];
  const text = (key) => String(raw[key] ?? "").trim();
  const date = (key, label, required) => {
    if (isBlankCell(raw[key])) {
      if (required) errors.push(`${label} is required`);
      return null;
    }
    const parsed = parseDate(raw[key]);
    if (!parsed) errors.push(`Invalid ${label} format`);
    return parsed;
  };

  const employeeId = text("employee_id");
  if (!employeeId) errors.push("Employee ID is required");

  const type = isBlankCell(raw.type) ? "Advance" : matchType(raw.type);
  if (!type) errors.push(`Type "${text("type")}" must be Advance or Loan`);

  let amount = null;
  if (isBlankCell(raw.amount)) errors.push("Amount is required");
  else {
    const number = toNumber(raw.amount);
    if (!Number.isFinite(number) || number <= 0) errors.push("Amount must be a number more than 0");
    else amount = round2(number);
  }

  const reason = text("reason");
  if (!reason) errors.push("Reason is required");

  const requestDate = date("request_date", "Request Date", false);

  const record = { employee_id: employeeId, type, advance_type: "Salary", amount, reason };
  const description = text("description");
  if (description.length > 200) errors.push("Description can be at most 200 characters");
  else if (description) record.description = description;
  if (requestDate) record.request_date = requestDate;

  // Advance: recovered in full in the payroll month (blank = this month).
  if (type === "Advance") {
    let month = moment().format("YYYY-MM");
    if (!isBlankCell(raw.payroll_month)) {
      month = parseMonth(raw.payroll_month);
      if (!month) errors.push("Invalid Payroll Month format (use e.g. Oct 2026)");
    }
    if (month) {
      record.payroll_month = moment(month, "YYYY-MM").startOf("month").format(API_DATE);
      record.repayment_mode = "Full";
      record.repayment_date = moment(month, "YYYY-MM").endOf("month").format(API_DATE);
    }
  }

  if (type === "Loan") {
    const mode = isBlankCell(raw.repayment_mode) ? null : matchMode(raw.repayment_mode);
    if (!mode) errors.push("Repayment Mode must be Full or Installment for a Loan");
    const repaymentDate = date("repayment_date", "Repayment Date", true);
    let frequency = null;
    if (!isBlankCell(raw.repayment_frequency)) {
      frequency = matchFrequency(raw.repayment_frequency);
      if (!frequency) errors.push("Repayment Frequency must be Weekly, Bi-weekly, Semi-monthly or Monthly");
    }
    record.repayment_mode = mode;
    record.repayment_date = repaymentDate;
    if (frequency) record.repayment_frequency = frequency;
    if (mode === "Installment") {
      const count = toNumber(raw.no_of_installments);
      if (!Number.isInteger(count)) errors.push("No. of Instalments must be a whole number");
      else if (count < 2) errors.push("Installment mode needs at least 2 instalments");
      else record.no_of_installments = count;
    }
  }

  return { rowNumber, raw, errors, row: errors.length ? null : record };
};

// Header + examples for the downloadable .xlsx (the API's own examples).
export const importTemplateRows = () => {
  const today = moment().format("DD MMM YYYY");
  const nextMonth = moment().add(1, "month").startOf("month").format("DD MMM YYYY");
  const payrollMonth = moment().format("MMM YYYY");
  // Same column order as IMPORT_FIELDS.
  return [
    IMPORT_FIELDS.map((field) => field.label),
    ["EMP001", "Loan", 20000, "Medical loan", "Medical emergency", today, "", "Installment", nextMonth, "Monthly", 4],
    ["EMP002", "Loan", 30000, "Two-wheeler loan", "Vehicle purchase", today, "", "Full", nextMonth, "", ""],
    ["EMP003", "Advance", 5000, "Festival advance", "Festival expenses", today, payrollMonth, "", "", "", ""],
  ];
};
